import React from 'react';
import { Euro, MapPin, Target, Heart, Calculator, TrendingUp, MessageCircle, Send } from 'lucide-react';

export function OpportunityCard({
  t,
  lang,
  opp,
  isSelected,
  onSelect,
  isFavourite,
  onToggleFavourite,
  targetCountry,
  reportOppId,
  setReportOppId,
  reportEmail,
  setReportEmail,
  reportMessage,
  setReportMessage,
  reportSending,
  reportSent,
  sendReport,
}) {
  const qualitySummary = lang === 'fr' ? (opp.qualitySummaryFr || opp.qualitySummary)
    : lang === 'pt' ? (opp.qualitySummaryPt || opp.qualitySummary)
    : lang === 'es' ? (opp.qualitySummaryEs || opp.qualitySummary)
    : opp.qualitySummary;
  return (
    <div
      onClick={() => onSelect(opp)}
      className="bg-slate-800 rounded-xl border border-slate-700 hover:border-emerald-500/50 transition-all cursor-pointer overflow-hidden"
    >
      <div className="p-3 min-[480px]:p-4 sm:p-6">

        {/* ── Small mobile layout (< 480px) ── */}
        <div className="flex min-[480px]:hidden items-start gap-3">
          <div className="w-14 h-14 bg-slate-700 rounded-lg flex items-center justify-center text-3xl overflow-hidden shrink-0">
            {opp.image && opp.image.startsWith('http') ? (
              <img src={opp.image} alt={`${opp.make} ${opp.model}`} className="w-full h-full object-cover" />
            ) : (
              <span>🚗</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                {/* Row 1: Make, Model */}
                <h3 className="text-base font-bold truncate">{opp.make} {opp.model}</h3>
                {/* Row 2: Year, Version, Fuel */}
                <p className="text-slate-400 text-xs truncate">
                  {opp.year} • {opp.version} • {opp.fuelType ? opp.fuelType.charAt(0).toUpperCase() + opp.fuelType.slice(1) : 'N/A'}
                </p>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); onToggleFavourite(opp.id); }}
                className="p-1 rounded-lg hover:bg-slate-700 transition-colors shrink-0"
                title={isFavourite ? t('removeFromFavourites') : t('addToFavourites')}
              >
                <Heart className={`w-5 h-5 transition-colors ${
                  isFavourite
                    ? 'text-rose-500 fill-current'
                    : 'text-slate-500 hover:text-rose-400'
                }`} />
              </button>
            </div>
            {/* Row 3: Price, Route, AI score */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs mt-1.5 text-slate-400">
              <span className="font-bold text-white">€{opp.sourcePrice?.toLocaleString() || 0}</span>
              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{opp.sourceCountry} → {opp.targetCountry}</span>
              {opp.qualityScore != null && (
                <div className="flex items-center gap-1">
                  <span className="text-[10px] font-semibold text-slate-400">AI</span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                    opp.qualityScore >= 8 ? 'bg-emerald-500/20 text-emerald-400' :
                    opp.qualityScore <= 4 ? 'bg-red-500/20 text-red-400' :
                    'bg-amber-500/20 text-amber-400'
                  }`}>
                    {opp.qualityScore >= 8 ? t('qualityHigh') : opp.qualityScore <= 4 ? t('qualityLow') : t('qualityMedium')}
                  </span>
                </div>
              )}
            </div>
            {/* Row 4: Profit + % */}
            <div className="flex items-baseline gap-2 mt-1.5">
              <div className="text-lg font-bold text-emerald-400">+€{opp.netProfit?.toLocaleString() || 0}</div>
              <div className="text-xs text-slate-400">{opp.profitMargin}% {t('profitSaving')}</div>
            </div>
            {/* Row 5: Similar listings */}
            <div className="flex items-center gap-1 text-xs mt-1">
              <Target className="w-3 h-3 text-emerald-400" />
              <span className="text-white">{opp.similarCarsInTarget} {t('similarIn')} {opp.targetCountry}</span>
            </div>
          </div>
        </div>

        {/* ── Larger mobile + desktop layout (≥ 480px) ── */}
        <div className="hidden min-[480px]:flex items-center gap-3 sm:gap-4">
          {/* Image — centered vertically */}
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-slate-700 rounded-lg flex items-center justify-center text-3xl sm:text-4xl overflow-hidden shrink-0">
            {opp.image && opp.image.startsWith('http') ? (
              <img src={opp.image} alt={`${opp.make} ${opp.model}`} className="w-full h-full object-cover" />
            ) : (
              <span>🚗</span>
            )}
          </div>
          {/* Left content — centered vertically */}
          <div className="flex-1 min-w-0">
            {/* Row 1: Make, Model */}
            <h3 className="text-lg sm:text-xl font-bold truncate">{opp.make} {opp.model}</h3>
            {/* Row 2: Year, Version, Fuel, Mileage */}
            <p className="text-slate-400 text-xs sm:text-sm truncate">
              {opp.year} • {opp.version} • {opp.fuelType ? opp.fuelType.charAt(0).toUpperCase() + opp.fuelType.slice(1) : 'N/A'} • {opp.mileage?.toLocaleString() || 0} km
            </p>
            {/* Row 3: Price, AI score */}
            <div className="flex flex-wrap items-center gap-x-3 sm:gap-x-4 gap-y-1 text-xs sm:text-sm mt-2">
              <div className="flex items-center gap-1.5">
                <Euro className="w-3.5 h-3.5 text-slate-400" />
                <span className="font-bold">€{opp.sourcePrice?.toLocaleString() || 0}</span>
              </div>
              {opp.qualityScore != null && (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-semibold text-slate-400">AI</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    opp.qualityScore >= 8 ? 'bg-emerald-500/20 text-emerald-400' :
                    opp.qualityScore <= 4 ? 'bg-red-500/20 text-red-400' :
                    'bg-amber-500/20 text-amber-400'
                  }`}>
                    {opp.qualityScore >= 8 ? t('qualityHigh') : opp.qualityScore <= 4 ? t('qualityLow') : t('qualityMedium')}
                  </span>
                </div>
              )}
            </div>
            {/* Row 4: Route */}
            <div className="flex items-center gap-1.5 text-xs sm:text-sm mt-1">
              <MapPin className="w-3.5 h-3.5 text-slate-400" />
              <span>{opp.sourceCountry} → {opp.targetCountry}</span>
            </div>
          </div>
          {/* Profit column — centered vertically */}
          <div className="flex flex-col items-end shrink-0">
            <div className="text-xl sm:text-2xl font-bold text-emerald-400 text-right leading-tight">+€{opp.netProfit?.toLocaleString() || 0}</div>
            <div className="text-[11px] sm:text-xs text-slate-400 text-right">{opp.profitMargin}% {t('profitSaving')}</div>
            <div className="flex items-center gap-1.5 text-[11px] sm:text-xs mt-1">
              <Target className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-white">{opp.similarCarsInTarget} {t('similarIn')} {opp.targetCountry}</span>
            </div>
          </div>
          {/* Favourite column — far right, centered vertically */}
          <div className="flex items-center shrink-0 ml-2 sm:ml-3">
            <button
              onClick={(e) => { e.stopPropagation(); onToggleFavourite(opp.id); }}
              className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors"
              title={isFavourite ? t('removeFromFavourites') : t('addToFavourites')}
            >
              <Heart className={`w-5 h-5 sm:w-6 sm:h-6 transition-colors ${
                isFavourite
                  ? 'text-rose-500 fill-current'
                  : 'text-slate-500 hover:text-rose-400'
              }`} />
            </button>
          </div>
        </div>

        {isSelected && (
          <div className="mt-6 pt-6 border-t border-slate-700">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Calculator className="w-4 h-4 text-emerald-400" />
                  {t('costBreakdown')}
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">{t('purchasePriceCountry', { country: opp.sourceCountry })}</span>
                    <span className="font-medium">€{opp.sourcePrice?.toLocaleString() || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">{t('transport')}</span>
                    <span className="font-medium">€{opp.transportCost?.toLocaleString() || 0}</span>
                  </div>
                  {opp.importTax > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">{t('importTax')}</span>
                      <span className="font-medium">€{opp.importTax?.toLocaleString() || 0}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-slate-400">{t('registration')}</span>
                    <span className="font-medium">€{opp.registrationFee?.toLocaleString() || 0}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-slate-600">
                    <span className="font-semibold">{t('totalCost')}</span>
                    <span className="font-semibold">€{((opp.sourcePrice || 0) + (opp.transportCost || 0) + (opp.importTax || 0) + (opp.registrationFee || 0)).toLocaleString()}</span>
                  </div>
                </div>
                <div className="mt-2 text-[11px] text-slate-500 italic">
                  {t('costDisclaimer')}
                </div>
                <div className="mt-3 pt-3 border-t border-slate-700">
                  <div className="text-xs text-slate-500 mb-1">{t('vehicleSpecs')}</div>
                  <div className="flex gap-4 text-xs">
                    <span className="text-slate-400">CO2: <span className={opp.co2 ? 'text-white' : 'text-amber-400'}>{opp.co2 ? `${opp.co2} g/km` : 'N/A'}</span></span>
                    <span className="text-slate-400">Engine: <span className={opp.engineCC ? 'text-white' : 'text-amber-400'}>{opp.engineCC ? `${opp.engineCC} cc` : 'N/A'}</span></span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-emerald-400" />
                  {t('revenueProjection')}
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">{t('medianPriceInWithCount', { country: targetCountry, count: opp.similarCarsInTarget })}</span>
                    <span className="font-medium">€{opp.expectedSalePrice?.toLocaleString() || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">{t('totalCosts')}</span>
                    <span className="font-medium">-€{((opp.sourcePrice || 0) + (opp.transportCost || 0) + (opp.importTax || 0) + (opp.registrationFee || 0)).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-slate-600 text-emerald-400">
                    <span className="font-semibold">{t('netProfitSaving')}</span>
                    <span className="font-bold text-lg">€{opp.netProfit?.toLocaleString() || 0}</span>
                  </div>
                  {qualitySummary ? (
                    <div className="mt-4 p-3 bg-slate-700/50 rounded-lg border border-slate-600">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs text-emerald-400 font-medium">{t('aiQualityScoreTitle')}</span>
                        {opp.qualityScore != null && (
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                            opp.qualityScore >= 8 ? 'bg-emerald-500/20 text-emerald-400' :
                            opp.qualityScore <= 4 ? 'bg-red-500/20 text-red-400' :
                            'bg-amber-500/20 text-amber-400'
                          }`}>
                            {opp.qualityScore >= 8 ? t('qualityHigh') : opp.qualityScore <= 4 ? t('qualityLow') : t('qualityMedium')} {opp.qualityScore}/10
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-300">{qualitySummary}</div>
                    </div>
                  ) : (
                    <div className="mt-4 p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                      <div className="text-xs text-emerald-400 mb-1">{t('aiQualityScoreTitle')}</div>
                      <div className="text-xs text-slate-300">{t('qualityNotAvailable')}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <a
                href={opp.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 bg-emerald-500 hover:bg-emerald-600 px-4 py-2 rounded-lg font-medium transition-colors text-center"
              >
                {t('viewSourceListing')}
              </a>
              <button
                onClick={(e) => { e.stopPropagation(); setReportOppId(reportOppId === opp.id ? null : opp.id); }}
                className="flex-1 bg-slate-700 hover:bg-slate-600 px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                <MessageCircle className="w-4 h-4" />
                {t('somethingWrong')}
              </button>
            </div>

            {reportOppId === opp.id && (
              <div className="mt-4 p-4 bg-slate-900/50 rounded-lg border border-slate-600" onClick={(e) => e.stopPropagation()}>
                {reportSent === opp.id ? (
                  <div className="text-center text-emerald-400 py-2">
                    {t('thanksReport')}
                  </div>
                ) : (
                  <>
                    <div className="text-sm font-medium mb-3">{t('reportIssue')}</div>
                    <div className="space-y-3">
                      <input
                        type="email"
                        placeholder={t('emailPlaceholder')}
                        value={reportEmail}
                        onChange={(e) => setReportEmail(e.target.value)}
                        className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:ring-2 focus:ring-emerald-500 outline-none"
                      />
                      <textarea
                        placeholder={t('reportPlaceholder')}
                        value={reportMessage}
                        onChange={(e) => setReportMessage(e.target.value)}
                        rows={3}
                        className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:ring-2 focus:ring-emerald-500 outline-none resize-none"
                      />
                      <button
                        onClick={() => sendReport(opp)}
                        disabled={reportSending || !reportEmail || !reportMessage}
                        className="bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 rounded-lg font-medium transition-colors text-sm flex items-center gap-2"
                      >
                        <Send className="w-4 h-4" />
                        {reportSending ? t('sending') : t('send')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default OpportunityCard;
